import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, fmtMoney, fmtDate, Empty } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, DotsThreeVertical, PencilSimple, Trash, Phone, ChatTeardropDots, ArrowSquareOut, CreditCard } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import FilesPanel from "@/components/FilesPanel";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { googleVoiceCallUrl, googleVoiceTextUrl } from "@/lib/googleVoice";

const empty = { name: "", org_type: "USCIS", client_type: "both", contact_name: "", email: "", phone: "", address: "", alien_number: "", hourly_rate: 0, preferred_language: "en", cancellation_policy: null, notes: "" };
const DEFAULT_CANCELLATION_TIERS = [
  { hours_before: 24, fee_type: "percent", fee_value: 50, label: "Late cancellation (< 24h)" },
  { hours_before: 12, fee_type: "percent", fee_value: 100, label: "Very late cancellation (< 12h)" },
  { hours_before: 0,  fee_type: "flat",    fee_value: 150, label: "No-show" },
];

export default function Clients() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => (await api.get("/clients")).data,
  });

  const save = useMutation({
    mutationFn: async (payload) => {
      if (editing) return (await api.patch(`/clients/${editing.client_id}`, payload)).data;
      return (await api.post("/clients", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success(editing ? "Client updated" : "Client added");
      setOpen(false); setForm(empty); setEditing(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const del = useMutation({
    mutationFn: async ({ id, force }) => (await api.delete(`/clients/${id}${force ? "?force=true" : ""}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clients"] }); qc.invalidateQueries({ queryKey: ["projects"] }); qc.invalidateQueries({ queryKey: ["invoices"] }); toast.success("Client removed"); },
    onError: (e, vars) => {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && typeof detail === "object") {
        const parts = [];
        if (detail.projects) parts.push(`${detail.projects} project${detail.projects === 1 ? "" : "s"}`);
        if (detail.invoices) parts.push(`${detail.invoices} invoice${detail.invoices === 1 ? "" : "s"}`);
        if (detail.communications) parts.push(`${detail.communications} communication${detail.communications === 1 ? "" : "s"}`);
        const msg = `This client has ${parts.join(", ")}. Deleting will remove all of them too. Continue?`;
        if (window.confirm(msg)) del.mutate({ id: vars.id, force: true });
      } else {
        toast.error(detail || "Delete failed");
      }
    },
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };

  // Send payment link dialog state
  const [payLinkClient, setPayLinkClient] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDescription, setPayDescription] = useState("");

  const sendPaymentLink = useMutation({
    mutationFn: async ({ client_id, amount, description }) =>
      (await api.post(`/clients/${client_id}/send-payment-link`, {
        amount, description,
        origin_url: window.location.origin,
        send_email: true,
      })).data,
    onSuccess: (d) => {
      toast.success(`Payment link emailed to ${d.sent_to || "client"}`);
      qc.invalidateQueries({ queryKey: ["communications"] });
      setPayLinkClient(null); setPayAmount(""); setPayDescription("");
      if (d.checkout_url) navigator.clipboard?.writeText(d.checkout_url)?.catch(() => {});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not send payment link"),
  });

  const openPaymentLink = (c) => {
    setPayLinkClient(c);
    setPayAmount("");
    setPayDescription(`Payment for ${c.name} services`);
  };

  return (
    <div data-testid="clients-page">
      <PageHeader
        eyebrow="Directory"
        title="Clients"
        description="Manage client accounts, contract rates, and contact history."
        actions={
          <Button onClick={openNew} data-testid="add-client-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
            <Plus size={16} weight="bold" className="mr-1.5" /> Add client
          </Button>
        }
      />

      {isLoading ? (
        <div className="card-flat p-10 text-center text-slate-400">Loading…</div>
      ) : clients.length === 0 ? (
        <Empty title="No clients yet" hint="Add your first client to start tracking contracts and rates." />
      ) : (
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Client</th>
                <th className="text-left px-3 py-3 font-semibold">Type</th>
                <th className="text-left px-3 py-3 font-semibold">Contact</th>
                <th className="text-left px-3 py-3 font-semibold">Email</th>
                <th className="text-right px-3 py-3 font-semibold">Hourly rate</th>
                <th className="text-left px-3 py-3 font-semibold">Added</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.client_id} className="data-row border-t border-slate-100" data-testid={`client-row-${c.client_id}`}>
                  <td className="px-5 py-3">
                    <Link to={`/clients/${c.client_id}`} className="font-medium text-slate-900 hover:text-blue-800 hover:underline" data-testid={`client-link-${c.client_id}`}>
                      {c.name}
                    </Link>
                    <div className="text-xs text-slate-500">{c.address || "—"}</div>
                  </td>
                  <td className="px-3 py-3 text-slate-700">
                    <div>{c.org_type}</div>
                    {c.client_type && c.client_type !== "both" && (
                      <span className={`badge-pill text-[10px] mt-0.5 ${c.client_type === "translation" ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                        {c.client_type === "translation" ? "Translation" : "Interpretation"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-slate-700">{c.contact_name || "—"}</td>
                  <td className="px-3 py-3 text-slate-700 font-mono text-xs">{c.email || "—"}</td>
                  <td className="px-3 py-3 text-right font-mono">{fmtMoney(c.hourly_rate)}</td>
                  <td className="px-3 py-3 text-slate-500 text-xs">{fmtDate(c.created_at)}</td>
                  <td className="px-5 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`client-actions-${c.client_id}`}>
                          <DotsThreeVertical size={16} weight="bold" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 bg-white">
                        <DropdownMenuLabel className="text-xs text-slate-500">Client actions</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                          <Link to={`/clients/${c.client_id}`} data-testid={`open-client-${c.client_id}`}>
                            <ArrowSquareOut size={14} className="mr-2" /> Open profile
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(c)} data-testid={`edit-client-${c.client_id}`}>
                          <PencilSimple size={14} className="mr-2" /> Edit client
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openPaymentLink(c)}
                          disabled={!c.email}
                          data-testid={`send-paylink-${c.client_id}`}
                          className="text-blue-800"
                        >
                          <CreditCard size={14} className="mr-2" /> Send payment link
                          {!c.email && <span className="ml-auto text-[10px] text-slate-400">no email</span>}
                        </DropdownMenuItem>
                        {c.phone && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-xs text-slate-500">Google Voice</DropdownMenuLabel>
                            <DropdownMenuItem asChild>
                              <a href={googleVoiceCallUrl(c.phone)} target="_blank" rel="noreferrer" data-testid={`call-client-${c.client_id}`}>
                                <Phone size={14} className="mr-2" /> Call {c.phone}
                              </a>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a href={googleVoiceTextUrl(c.phone)} target="_blank" rel="noreferrer" data-testid={`text-client-${c.client_id}`}>
                                <ChatTeardropDots size={14} className="mr-2" /> Text {c.phone}
                              </a>
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={(e) => {
                            e.preventDefault();
                            setTimeout(() => {
                              if (window.confirm(`Delete client "${c.name}"? This cannot be undone.`)) del.mutate({ id: c.client_id, force: false });
                            }, 50);
                          }}
                          className="text-red-600"
                          data-testid={`delete-client-${c.client_id}`}
                        >
                          <Trash size={14} className="mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit client" : "New client"}</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-4" data-testid="client-form">
            <Field label="Name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" />
            </Field>
            <Field label="Type">
              <Select value={form.org_type} onValueChange={(v) => setForm({ ...form, org_type: v })}>
                <SelectTrigger data-testid="client-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["USCIS","Medical","Legal","Other"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Services this client uses">
              <Select value={form.client_type || "both"} onValueChange={(v) => setForm({ ...form, client_type: v })}>
                <SelectTrigger data-testid="client-service-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="interpretation">Interpretation only</SelectItem>
                  <SelectItem value="translation">Translation only</SelectItem>
                  <SelectItem value="both">Both interpretation & translation</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">
                Used to filter the client list when creating an interpretation or translation project.
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contact name"><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Field>
              <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            </div>
            <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Alien Registration # (A#)">
              <Input placeholder="A123 456 789" value={form.alien_number} onChange={(e) => setForm({ ...form, alien_number: e.target.value })} data-testid="client-alien" />
            </Field>
            <Field label="Contract hourly rate (USD)">
              <Input type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value || 0) })} data-testid="client-rate" />
            </Field>
            <Field label="Preferred agreement language">
              <Select value={form.preferred_language || "en"} onValueChange={(v) => setForm({ ...form, preferred_language: v })}>
                <SelectTrigger data-testid="client-preferred-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en" data-testid="client-lang-en">English</SelectItem>
                  <SelectItem value="es" data-testid="client-lang-es">Español (Spanish)</SelectItem>
                  <SelectItem value="fr" data-testid="client-lang-fr">Français (French)</SelectItem>
                  <SelectItem value="ht" data-testid="client-lang-ht">Kreyòl (Haitian Creole)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">
                Contracts generated for this client will default to this language.
              </div>
            </Field>
            <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>

            {/* Cancellation policy — tiered fees */}
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="cancellation-policy-editor">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs font-semibold text-slate-900">Cancellation policy</div>
                  <div className="text-[11px] text-slate-500">If a project is cancelled inside these windows, an invoice with the matching fee is auto-created.</div>
                </div>
                {!form.cancellation_policy && (
                  <Button
                    size="sm" variant="outline" type="button"
                    onClick={() => setForm({ ...form, cancellation_policy: DEFAULT_CANCELLATION_TIERS.map((t) => ({ ...t })) })}
                    data-testid="use-default-policy-btn"
                  >
                    Use defaults
                  </Button>
                )}
              </div>
              {(form.cancellation_policy || []).length === 0 ? (
                <div className="text-[11px] text-slate-400 italic">No policy set — the system default (24h/50% · 12h/100% · no-show flat $150) applies.</div>
              ) : (
                <div className="space-y-2">
                  {form.cancellation_policy.map((t, idx) => (
                    <div key={idx} className="bg-white border border-slate-200 rounded-md p-2 grid grid-cols-12 gap-2 items-end" data-testid={`policy-tier-${idx}`}>
                      <div className="col-span-4">
                        <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Label</div>
                        <Input value={t.label || ""} onChange={(e) => {
                          const next = [...form.cancellation_policy]; next[idx] = { ...t, label: e.target.value };
                          setForm({ ...form, cancellation_policy: next });
                        }} className="h-8 text-sm" />
                      </div>
                      <div className="col-span-3">
                        <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Within (h)</div>
                        <Input type="number" min="0" value={t.hours_before} onChange={(e) => {
                          const next = [...form.cancellation_policy]; next[idx] = { ...t, hours_before: parseInt(e.target.value || 0) };
                          setForm({ ...form, cancellation_policy: next });
                        }} className="h-8 text-sm font-mono" />
                      </div>
                      <div className="col-span-2">
                        <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Type</div>
                        <Select value={t.fee_type} onValueChange={(v) => {
                          const next = [...form.cancellation_policy]; next[idx] = { ...t, fee_type: v };
                          setForm({ ...form, cancellation_policy: next });
                        }}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="flat">Flat $</SelectItem>
                            <SelectItem value="percent">Percent %</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Value</div>
                        <Input type="number" step="0.01" min="0" value={t.fee_value} onChange={(e) => {
                          const next = [...form.cancellation_policy]; next[idx] = { ...t, fee_value: parseFloat(e.target.value || 0) };
                          setForm({ ...form, cancellation_policy: next });
                        }} className="h-8 text-sm font-mono" />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, cancellation_policy: form.cancellation_policy.filter((_, i) => i !== idx) })}
                          className="text-slate-400 hover:text-red-600 p-1"
                          title="Remove"
                        >✕</button>
                      </div>
                    </div>
                  ))}
                  <Button
                    size="sm" variant="outline" type="button"
                    onClick={() => setForm({ ...form, cancellation_policy: [...(form.cancellation_policy || []), { hours_before: 24, fee_type: "flat", fee_value: 100, label: "" }] })}
                    className="text-xs"
                  >
                    + Add tier
                  </Button>
                </div>
              )}
            </div>

            {editing && (
              <div className="pt-4 border-t border-slate-200">
                <FilesPanel entityType="client" entityId={editing.client_id} />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => save.mutate(form)} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="client-save">
                {editing ? "Save changes" : "Create client"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Send payment link dialog */}
      <Dialog open={!!payLinkClient} onOpenChange={(o) => { if (!o) { setPayLinkClient(null); setPayAmount(""); setPayDescription(""); } }}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send payment link</DialogTitle>
          </DialogHeader>
          {payLinkClient && (
            <div className="space-y-4 py-2" data-testid="paylink-dialog">
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs">
                <div className="font-semibold text-slate-900">{payLinkClient.name}</div>
                <div className="text-slate-500 font-mono">{payLinkClient.email}</div>
              </div>
              <Field label="Amount (USD)">
                <Input
                  type="number" step="0.01" min="0.50" inputMode="decimal"
                  placeholder="e.g. 250.00"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  data-testid="paylink-amount"
                  autoFocus
                />
              </Field>
              <Field label="Description">
                <Textarea
                  rows={2}
                  placeholder="e.g. Interpretation services — August 15 USCIS appointment"
                  value={payDescription}
                  onChange={(e) => setPayDescription(e.target.value)}
                  data-testid="paylink-description"
                />
              </Field>
              <div className="text-[11px] text-slate-500 leading-relaxed">
                A branded email with a "Pay ${(parseFloat(payAmount) || 0).toFixed(2)} securely" button will be sent to <b>{payLinkClient.email}</b>. Payment goes to your live Stripe account.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayLinkClient(null)} data-testid="paylink-cancel">Cancel</Button>
            <Button
              className="bg-blue-900 hover:bg-blue-800 text-white"
              disabled={sendPaymentLink.isPending || !payAmount || parseFloat(payAmount) < 0.50}
              onClick={() => sendPaymentLink.mutate({
                client_id: payLinkClient.client_id,
                amount: parseFloat(payAmount),
                description: payDescription || "Unity Language Group — Payment",
              })}
              data-testid="paylink-send"
            >
              <CreditCard size={14} className="mr-2" />
              {sendPaymentLink.isPending ? "Sending…" : "Send payment link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
